import type { EditorCanvas } from "replo-utils/lib/misc/canvas";
import type { IndividualCanvasState } from "./canvas-types";

import * as React from "react";

import DeprecatedIconButton from "@v1/components/designSystem/button/IconButton";
import { Checkbox } from "@v1/components/designSystem/checkbox/Checkbox";
import { HotkeyIndicator } from "@v1/components/designSystem/hotkeys/HotKeyIndicator";
import { Menu, MenuTrigger } from "@v1/components/designSystem/menu/Menu";
import Popover from "@v1/components/designSystem/popover/Popover";
import { setEditorMode } from "@v1/reducers/core-reducer";
import { useEditorDispatch, useEditorSelector } from "@v1/store/store";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { List, Play } from "lucide-react";
import { clamp } from "replo-utils/lib/math";
import { preventDefault } from "replo-utils/lib/misc";
import { capitalizeFirstLetter } from "replo-utils/lib/string";

import {
  CANVAS_DATA,
  CANVAS_FRAME_GAP,
  CANVAS_NAVBAR_HEIGHT,
  CANVAS_NAVBAR_VERTICAL_SPACING,
} from "./canvas-constants";
import {
  hideCanvas,
  selectCanvasDeltaXY,
  selectCanvases,
  selectCanvasesKeys,
  selectCanvasFrameWidths,
  selectCanvasInteractionMode,
  selectCanvasScale,
  selectVisibleCanvases,
  setPreviewWidth,
  showCanvas,
} from "./canvas-reducer";
import { CustomViewportControl } from "./CanvasControls";

export function CanvasHeaderBar() {
  const visibleCanvases = useEditorSelector(selectVisibleCanvases);
  const canvasScale = useEditorSelector(selectCanvasScale);
  const { deltaX, deltaY } = useEditorSelector(selectCanvasDeltaXY);
  const canvasInteractionMode = useEditorSelector(selectCanvasInteractionMode);
  // Note (Evan, 2024-10-01): When the canvas is locked (during AI streaming), we
  // hide this along with other aspects of the editor UI like sidebars
  if (canvasInteractionMode === "locked") {
    return null;
  }

  // Note (Noah, 2024-07-01): We want the nav bar to "stick" to the top of
  // the canvas with a gap, but when you zoom out or in too far, just
  // scaling this gap with the canvasScale makes it a little too big, so
  // we clamp it to a reasonable size.
  const yOffset =
    // First offset by deltaY so it's even with the top of the canvas
    deltaY -
    // Then offset by the navbar height so that the navbar will stick exactly
    // to the top of the canvas
    CANVAS_NAVBAR_HEIGHT -
    // Then offset by the gap so there's a bit of space between the navbar and
    // the top of the canvas
    clamp(
      CANVAS_NAVBAR_VERTICAL_SPACING * canvasScale,
      6,
      CANVAS_NAVBAR_VERTICAL_SPACING,
    );
  return (
    <div
      className="absolute left-0 top-0 flex origin-[0_0]"
      style={{
        transform: `translate3d(${deltaX}px, ${yOffset}px, 0px)`,
        gap: CANVAS_FRAME_GAP * canvasScale,
      }}
    >
      <DevicesToolbar />
      {Object.entries(visibleCanvases).map(([key, canvas]) => (
        <CanvasNavbar
          key={key}
          canvasKey={key as EditorCanvas}
          canvasState={canvas}
        />
      ))}
    </div>
  );
}

function CanvasNavbar({
  canvasKey,
  canvasState,
}: {
  canvasKey: EditorCanvas;
  canvasState: IndividualCanvasState;
}) {
  const dispatch = useEditorDispatch();
  const canvasScale = useEditorSelector(selectCanvasScale);
  const visibleCanvasesFrameWidth = useEditorSelector(selectCanvasFrameWidths);
  const logEvent = useLogAnalytics();

  function previewCanvas() {
    logEvent("canvas.preview", {
      source: "frameHeader",
      frame: canvasKey,
    });
    dispatch(setPreviewWidth(canvasState.canvasWidth));
    dispatch(setEditorMode("preview"));
  }
  const canvasName = capitalizeFirstLetter(canvasKey);

  const visibleCanvases = useEditorSelector(selectVisibleCanvases);
  const isOnlyOneCanvasActive = Object.values(visibleCanvases).length === 1;
  const width = visibleCanvasesFrameWidth[canvasKey];
  return (
    <div
      className="@container flex rounded-sm bg-white py-2 shadow-lg"
      style={{
        width: width * canvasScale,
      }}
    >
      <Menu
        menuType="context"
        contentClassNames="w-full"
        items={[
          {
            type: "leaf",
            id: "hide-canvas",
            title: `Hide ${canvasName}`,
            onSelect: () => {
              dispatch(hideCanvas(canvasKey));
            },
            disabled: isOnlyOneCanvasActive,
            variant: "default",
            size: "sm",
          },
        ]}
        trigger={
          <MenuTrigger className="flex w-full cursor-default bg-transparent">
            <div className="@[100px]:justify-between @[100px]:pr-2 @[100px]:pl-3 flex w-full items-center justify-center px-1">
              <div className="@[100px]:flex hidden items-center gap-4">
                <div className="text-default @[100px]:block hidden text-sm font-bold">
                  {canvasName}
                </div>
                <div className="@[210px]:block hidden">
                  <CustomViewportControl canvas={canvasKey} />
                </div>
              </div>
              <DeprecatedIconButton
                id="preview-button"
                variant="secondary"
                size="base"
                layoutClassName="h-8 w-8"
                icon={<Play size={14} />}
                tooltipText={<HotkeyIndicator hotkey="togglePreviewMode" />}
                onClick={previewCanvas}
              />
            </div>
          </MenuTrigger>
        }
      />
    </div>
  );
}

function DevicesToolbar() {
  const canvasesKeys = useEditorSelector(selectCanvasesKeys);
  const canvasScale = useEditorSelector(selectCanvasScale);

  return (
    <div
      className="absolute right-full top-0"
      style={{
        marginRight: CANVAS_FRAME_GAP * canvasScale,
      }}
    >
      <Popover
        triggerAsChild
        shouldPreventDefaultOnInteractOutside={false}
        content={
          <fieldset className="flex flex-col gap-3">
            <legend className="sr-only">Toggle devices</legend>
            <form className="contents" onSubmit={preventDefault}>
              {canvasesKeys.map((canvas) => (
                <CanvasCheckbox key={canvas} canvas={canvas as EditorCanvas} />
              ))}
            </form>
          </fieldset>
        }
        side="bottom"
        title="Show Devices"
        className="min-w-40"
        align="start"
        alignOffset={-10}
        stayInPosition
      >
        <button
          type="button"
          aria-label="Show Devices List"
          className="text-muted hover:text-muted rounded-sm bg-white p-3 shadow-lg"
        >
          <List size={24} />
        </button>
      </Popover>
    </div>
  );
}

function CanvasCheckbox({ canvas }: { canvas: EditorCanvas }) {
  const dispatch = useEditorDispatch();
  const canvases = useEditorSelector(selectCanvases);
  const logAnalytics = useLogAnalytics();
  const id = `toggle-${canvas}${React.useId()}`;

  const currentCanvas = Object.entries(canvases).find(
    ([key]) => key === canvas,
  )?.[1];

  if (!currentCanvas) {
    return null;
  }

  const isOnlyOneChecked =
    Object.values(canvases).filter((c) => c.isVisible).length === 1;
  const isDisabled = isOnlyOneChecked && currentCanvas?.isVisible;

  const onCheckedChange = (isChecked: boolean) => {
    if (isDisabled) {
      return;
    }
    logAnalytics("canvas.frame.toggle", {
      action: isChecked ? "show" : "hide",
      frame: canvas,
    });
    if (isChecked) {
      dispatch(
        showCanvas({
          canvas,
          width: CANVAS_DATA[canvas].defaultFrameWidth,
        }),
      );
    } else {
      dispatch(hideCanvas(canvas));
    }
  };

  return (
    <div className="flex items-center gap-1.5">
      <Checkbox
        checked={currentCanvas.isVisible}
        onChange={onCheckedChange}
        id={id}
        disabled={isDisabled}
      />
      <label
        className="cursor-pointer select-none text-xs font-normal"
        htmlFor={id}
      >
        {capitalizeFirstLetter(canvas)}
      </label>
    </div>
  );
}
